import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { DrawerNavigationProp } from '@react-navigation/drawer'
import { useSelector } from 'react-redux'
import { Feature, useFeatureFlag } from '@src/components'
import {
  useActiveCoreSubscription,
  useActiveSubscriptions,
} from '@src/hooks/useActiveSubscriptions'
import {
  AppStackParamList,
  CommonAuthorizedStackParamList,
  DrawerParamList,
} from '@src/navigation/types'
import { productsByCategorySelector } from '@src/selectors/app'
import { useAllowedAddonQuantity } from '@src/utils/hooks'
import { useDispatchAsync } from '@src/utils/dispatch'
import { openUrl } from '@src/utils/openUrl'
import { BillingProduct, BillingProductCategory, Product } from '@src/types'
import { getCancelAddonModalTitle } from '../utils'
import { resolveSteloAddonProducts } from './useSteloCgmProducts'

export const useSelectedAddonModal = () => {
  const dispatchAsync = useDispatchAsync()
  const navigation = useNavigation<
    DrawerNavigationProp<DrawerParamList> &
      StackNavigationProp<CommonAuthorizedStackParamList & AppStackParamList>
  >()
  const { getAllowedAddonQuantity } = useAllowedAddonQuantity()
  const subscription = useActiveCoreSubscription()
  const activeSubscriptions = useActiveSubscriptions()
  const steloAddonOfferEnabled = useFeatureFlag(Feature.SteloCgmAddonOffer)
  const steloCgmProducts = useSelector(productsByCategorySelector(BillingProductCategory.SteloCgm))

  const hasActiveCgmSubscription = activeSubscriptions.some(
    (activeSubscription) => activeSubscription.primaryProduct.includesCgm,
  )

  return async (product: Product) => {
    const { allowedAddonQuantity, addonLimit } = getAllowedAddonQuantity(product.id)

    if (product.url) {
      openUrl(product.url)
      return
    }

    if (allowedAddonQuantity === 0) {
      if (subscription?.paused) {
        const title =
          'You must unpause your subscription before adding new products.' +
          ' Please reach out to our support team if you need help.'
        navigation.navigate('CancelModal', {
          title,
          cancelText: 'Manage Subscription',
          parentScreen: 'Marketplace',
          cancelScreen: 'ProgramDetails',
          cancelScreenParams: { subscriptionId: subscription.id },
        })
        return
      }

      navigation.navigate('CancelModal', {
        title: getCancelAddonModalTitle(addonLimit ?? 0),
        cancelText: 'Contact Support',
        confirmText: 'Nevermind',
        parentScreen: 'Marketplace',
      })
      return
    }

    const productsSelectionParams = {
      selectedAddon: product,
      addons: [product],
    }

    const showSteloAddonOfferScreen =
      steloAddonOfferEnabled && product.key === BillingProduct.SteloCgm_2

    if (!showSteloAddonOfferScreen) {
      navigation.navigate('ProductsSelection', productsSelectionParams)
      return
    }

    let steloProducts = steloCgmProducts

    if (steloProducts.length === 0) {
      try {
        steloProducts = await dispatchAsync<Product[]>({
          type: 'app/fetchProducts',
          payload: { category: BillingProductCategory.SteloCgm },
        })
      } catch {
        steloProducts = []
      }
    }

    const steloAddonOfferParams = { steloProducts }

    if (hasActiveCgmSubscription) {
      navigation.navigate('SteloAddonOffer', steloAddonOfferParams)
      return
    }

    const { recurringProduct } = resolveSteloAddonProducts(steloProducts)

    if (recurringProduct) {
      navigation.navigate('SteloAddonOffer', steloAddonOfferParams)
      return
    }

    navigation.navigate('ProductsSelection', productsSelectionParams)
  }
}
